<template>
    <div class="flex flex-col min-h-screen overflow-y-auto h-screen bg-surface text-ink">
        <Head title="Sign In" />
        <div class="fade-in grow justify-center gap-5 md:gap-10 flex flex-col items-center py-4 md:py-10">
            <div class="flex flex-col gap-4 justify-around text-center max-w-sm mx-auto px-4">
                <div class="flex flex-col pb-5 justify-center">
                    <img :src="Logo" alt="Valence logo" class="mx-auto h-16 w-auto" />
                </div>
            </div>
            <!-- Arrives after the login check, when the user is waiting on the spinner with nothing
                 focused, so it keeps BaseNotification's own role="alert": an alert announces on
                 insertion, which a status created together with its text does not. -->
            <BaseNotification v-if="maintenance_message" theme="warning" hide-dismiss class="max-w-md text-center">
                <template #content
                    ><p class="body-small">{{ maintenance_message }}</p></template
                >
            </BaseNotification>
            <template v-if="error">
                <BaseNotification v-if="error === 'UNRECOGNIZED_EMAIL'" theme="error" hide-dismiss class="max-w-md">
                    <template #content
                        ><div class="body-small">
                            <p>
                                <b>Your email was not recognized.</b> Would you like to talk to our Sales Team?
                                <a href="https://www.valence.co/demo/lets-chat" class="underline">Submit an inquiry.</a>
                            </p>
                            <p class="body-small italic mt-2">If you believe you got this message by mistake, please try logging in again using your work email.</p>
                        </div></template
                    >
                </BaseNotification>
                <BaseNotification v-else theme="error" hide-dismiss class="max-w-md">
                    <template #content
                        ><p class="body-small">{{ error }}</p></template
                    >
                </BaseNotification>
            </template>
            <template v-if="sdkError">
                <BaseNotification theme="error" hide-dismiss class="max-w-md">
                    <template #content
                        ><p class="body-small">{{ sdkError }} We're unable to authenticate you. Please try again later.</p></template
                    >
                </BaseNotification>
            </template>
            <template v-if="consentError">
                <BaseNotification theme="error" hide-dismiss class="max-w-md">
                    <template #content
                        ><p class="body-small">{{ consentError }}</p></template
                    >
                </BaseNotification>
            </template>
            <!-- The notification below appears together with its text, so it cannot announce itself;
                 this region stays mounted and the notification is downgraded to a note so the two
                 do not compete. -->
            <span class="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="code-sent-announcement">{{ messageAnnouncement }}</span>
            <template v-if="message">
                <BaseNotification theme="success" hide-dismiss role="note" class="max-w-md">
                    <template #content
                        ><p class="body-small">{{ message }}</p></template
                    >
                </BaseNotification>
                <div v-if="timer > 0" class="body-small text-ink-muted mt-2">You can request another code in {{ timer }}s.</div>
                <BaseButton v-if="timer == 0" theme="primary" class="mt-2" @click="loginViaEmail">Request another code</BaseButton>
                <form @submit.prevent="verifyOTP">
                    <div class="flex flex-col gap-4 items-center">
                        <label class="flex gap-2 items-center" for="otp">
                            Code
                            <BaseInput id="otp" v-model="otp" name="otp" type="text" class="w-32" />
                        </label>
                        <LoadingSpinner v-if="loading" class="w-10 h-10" />
                        <BaseButton v-else theme="primary" :disabled="(should_show_terms && !terms) || !otp" class="w-32" type="submit"> Continue </BaseButton>
                    </div>
                </form>
            </template>
            <template v-else-if="!loading && !error && !sdkError && !maintenance_message && token">
                <div class="flex flex-col gap-4 items-center max-w-md">
                    <div class="flex flex-col gap-3 w-full">
                        <BaseButton theme="primary" class="w-full" :disabled="should_show_terms && !terms" @click="login"> Login </BaseButton>
                    </div>
                </div>
            </template>
            <LoadingSpinner v-else-if="loading" class="w-10 h-10" />
            <!-- sr-only keeps this persistent live region out of layout. It must stay mounted and
                 outside the v-if below: a live region that appears together with its own text is
                 not reliably announced, and the terms step arrives asynchronously (the consent
                 probe, or a re-prompt returned with a rejected claim) without a focus change. -->
            <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ termsAnnouncement }}</span>

            <Transition name="fade" mode="out-in">
                <div v-if="should_show_terms" class="form-control max-w-md mx-auto">
                    <TeamsTerms v-model:terms="terms" :terms-content="organization_terms_content" :modal="modal" :reconsent-reason="reconsentReason" />
                </div>
            </Transition>

            <div class="flex flex-row justify-center">
                <a
                    rel="noopener"
                    href="https://www.valence.co/other/privacy"
                    target="_blank"
                    class="inline-flex items-center gap-1 hover:opacity-90 italic underline body-small border-r border-r-border-strong px-4"
                    >Valence Privacy Policy<span class="bi bi-box-arrow-up-right text-xs"></span>
                </a>
                <a :href="feedback_mailto" class="inline-flex items-center gap-1 hover:opacity-90 italic underline body-small px-4" @click="logReportIssue">
                    Report an Issue <span class="bi bi-box-arrow-up-right text-xs"></span>
                </a>
            </div>
        </div>
        <div id="login-content" class="mt-5 px-4"></div>
        <TestimonialCarousel class="pt-10 md:pt-10">
            <template #testimonials>
                <BaseCard
                    theme="muted"
                    padding="none"
                    radius="xl"
                    class="relative items-center mx-4 h-full flex flex-col justify-between gap-2 text-ink text-center w-full pt-10 pb-3 md:pb-6 px-4 md:px-10 min-w-62.5">
                    <img loading="lazy" height="50" width="50" class="absolute top-[-25px] mx-auto left-0 right-0" :src="Profile1Image" alt="Profile photo" />
                    <p>"We have a lot of deadlines coming up this month. How can I help my team <strong>prioritize and feel less overwhelmed</strong>?"</p>
                </BaseCard>
                <BaseCard
                    theme="muted"
                    padding="none"
                    radius="xl"
                    class="relative items-center mx-4 h-full flex flex-col justify-between gap-2 text-ink text-center w-full pt-10 pb-3 md:pb-6 px-4 md:px-10 min-w-62.5">
                    <img loading="lazy" height="50" width="50" class="absolute top-[-25px] mx-auto left-0 right-0" :src="Profile2Image" alt="Profile photo" />
                    <p>"My co-worker keeps <strong>interrupting others in our meetings</strong>. How can I keep things on track?"</p>
                </BaseCard>
                <BaseCard
                    theme="muted"
                    padding="none"
                    radius="xl"
                    class="relative items-center mx-4 h-full flex flex-col justify-between gap-2 text-ink text-center w-full pt-10 pb-3 md:pb-6 px-4 md:px-10 min-w-62.5">
                    <img loading="lazy" height="50" width="50" class="absolute top-[-25px] mx-auto left-0 right-0" :src="Profile3Image" alt="Profile photo" />
                    <p>"How do I <strong>tell my employee that they need to stop missing important details</strong> without hurting their feelings?"</p>
                </BaseCard>
                <BaseCard
                    theme="muted"
                    padding="none"
                    radius="xl"
                    class="relative items-center mx-4 h-full flex flex-col justify-between gap-2 text-ink text-center w-full pt-10 pb-3 md:pb-6 px-4 md:px-10 min-w-62.5">
                    <img loading="lazy" height="50" width="50" class="absolute top-[-25px] mx-auto left-0 right-0" :src="Profile4Image" alt="Profile photo" />
                    <p>"I'm <strong>frustrated my manager keeps changing my team's priorities</strong> but I don't know how to tell them."</p>
                </BaseCard>
                <BaseCard
                    theme="muted"
                    padding="none"
                    radius="xl"
                    class="relative items-center mx-4 h-full flex flex-col justify-between gap-2 text-ink text-center w-full pt-10 pb-3 md:pb-6 px-4 md:px-10 min-w-62.5">
                    <img loading="lazy" height="50" width="50" class="absolute top-[-25px] mx-auto left-0 right-0" :src="Profile5Image" alt="Profile photo" />
                    <p>"<strong>My manager only gives me constructive feedback</strong> and it makes me feel like I'm bad at my job. How can I bring this up?"</p>
                </BaseCard>
                <BaseCard
                    theme="muted"
                    padding="none"
                    radius="xl"
                    class="relative items-center mx-4 h-full flex flex-col justify-between gap-2 text-ink text-center w-full pt-10 pb-3 md:pb-6 px-4 md:px-10 min-w-62.5">
                    <img loading="lazy" height="50" width="50" class="absolute top-[-25px] mx-auto left-0 right-0" :src="Profile6Image" alt="Profile photo" />
                    <p>"<strong>I need to announce a change and I know my team is going to be unhappy about it.</strong> How can I manage their reactions?"</p>
                </BaseCard>
                <BaseCard
                    theme="muted"
                    padding="none"
                    radius="xl"
                    class="relative items-center mx-4 h-full flex flex-col justify-between gap-2 text-ink text-center w-full pt-10 pb-3 md:pb-6 px-4 md:px-10 min-w-62.5">
                    <img loading="lazy" height="50" width="50" class="absolute top-[-25px] mx-auto left-0 right-0" :src="Profile7Image" alt="Profile photo" />
                    <p>"<strong>My co-worker has been really overwhelmed lately.</strong> What can I do to support them better?"</p>
                </BaseCard>
                <BaseCard
                    theme="muted"
                    padding="none"
                    radius="xl"
                    class="relative items-center mx-4 h-full flex flex-col justify-between gap-2 text-ink text-center w-full pt-10 pb-3 md:pb-6 px-4 md:px-10 min-w-62.5">
                    <img loading="lazy" height="50" width="50" class="absolute top-[-25px] mx-auto left-0 right-0" :src="Profile8Image" alt="Profile photo" />
                    <p>"<strong>My global team is struggling to coordinate our work together.</strong> What can I do to keep things organized?"</p>
                </BaseCard>
            </template>
        </TestimonialCarousel>
    </div>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { Head, router } from "@inertiajs/vue3";
import { app, authentication } from "@microsoft/teams-js";
import { computedAsync } from "@vueuse/core";
import Logo from "~img/color-logo.svg";
import Profile1Image from "~img/profile-1.png";
import Profile2Image from "~img/profile-2.png";
import Profile3Image from "~img/profile-3.png";
import Profile4Image from "~img/profile-4.png";
import Profile5Image from "~img/profile-5.png";
import Profile6Image from "~img/profile-6.png";
import Profile7Image from "~img/profile-7.png";
import Profile8Image from "~img/profile-8.png";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import TeamsTerms from "~vue/components/TermsConsent.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import TestimonialCarousel from "~vue/Onboarding/TestimonialCarousel.vue";
import { isInTeams } from "~vue/utils";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { markPagePublic, unmarkPagePublic } from "~vue/utils/sessionEnded";
import { termsConsentAnnouncement } from "~vue/utils/termsConsentAnnouncement";
import { computed, onUnmounted, ref, watch } from "vue";

// Signed-out is this page's normal state, so the token endpoint's 403 is expected rather than a
// session that ended. Without this the dialog opens over the login page and its only action
// navigates to the web login, which cannot complete inside the Teams iframe (BUG-979).
//
// Released on unmount because an already-authenticated user leaves this page by `router.visit()`
// below -- an Inertia swap, not a page load -- so the opt-out would otherwise persist onto the
// chat page and swallow a genuine session expiry there.
markPagePublic();
onUnmounted(unmarkPagePublic);

const {
    message,
    terms_are_required,
    org_terms_content,
    terms_document_id,
    modal_terms,
    reconsent_reason,
    teams_next_path: next_path,
    in_eu_server,
    base_url,
} = defineProps({
    feedback_mailto: {
        type: String,
        default: null,
    },
    message: {
        type: String,
        default: null,
    },
    error: {
        type: String,
        default: null,
    },
    terms_are_required: {
        type: Boolean,
        default: false,
    },
    org_terms_content: {
        type: String,
        default: null,
    },
    terms_document_id: {
        type: String,
        default: null,
    },
    modal_terms: {
        type: Boolean,
        default: false,
    },
    reconsent_reason: {
        type: String,
        default: "first_time",
    },
    teams_next_path: {
        type: String,
        default: null,
    },
    in_eu_server: {
        type: Boolean,
        default: false,
    },
    base_url: {
        type: String,
        required: true,
    },
});

const SSO_WINDOW_NAME = "SSO";

// Validate a Teams deep-link subEntityId as a same-origin path (including query/hash).
// subEntityId is essentially attacker-shaped input: anyone who can send a Graph activity
// notification to this user can choose its value. We rely on the URL constructor plus a
// strict prefix check to reject protocol-relative URLs ("//evil.com"), backslash escapes
// ("/\\evil.com" that some browsers normalize to protocol-relative), javascript:/data:
// schemes, and cross-origin absolute URLs.
function safeSubPagePath(subPageId, baseUrl) {
    if (typeof subPageId !== "string" || !subPageId) {
        return null;
    }
    if (!/^\/(?![/\\])/.test(subPageId)) {
        return null;
    }
    try {
        const url = new URL(subPageId, baseUrl);
        const base = new URL(baseUrl);
        if (url.origin !== base.origin) {
            return null;
        }
        return url.pathname + url.search + url.hash;
    } catch {
        return null;
    }
}

const loading = ref(false);
const loginCheckComplete = ref(false);
const maintenance_message = ref(null);
const otp = ref("");
const sdkError = ref(null);
// Kept separate from sdkError: a consent failure must not hide the Login button, or the user is
// dead-ended with no way to retry.
const consentError = ref(null);
const terms = ref(false);
const timer = ref(60);
const messageAnnouncement = ref("");
const organization_terms_content = ref(org_terms_content);
const termsDocumentId = ref(terms_document_id);
const modal = ref(modal_terms);
const reconsentReason = ref(reconsent_reason);

const deepLinkPath = ref(null);

// Deep-link persistence key. localStorage survives full-page navigations (SSO polling
// hits window.location.href, OTP verify triggers an Inertia redirect that unmounts this
// page) — the in-memory ref alone would lose the target for fresh-auth users. Same
// origin, cleared as soon as we use it.
const DEEP_LINK_STORAGE_KEY = "ms_teams_pending_deep_link";

function loadStoredDeepLinkPath() {
    try {
        const stored = globalThis.localStorage?.getItem(DEEP_LINK_STORAGE_KEY);
        return stored ? safeSubPagePath(stored, base_url) : null;
    } catch {
        return null;
    }
}

function storeDeepLinkPath(path) {
    try {
        globalThis.localStorage?.setItem(DEEP_LINK_STORAGE_KEY, path);
    } catch {
        // localStorage disabled/unavailable — falls back to the ?next= mirror + in-memory ref.
    }
}

function clearStoredDeepLinkPath() {
    try {
        globalThis.localStorage?.removeItem(DEEP_LINK_STORAGE_KEY);
    } catch {
        // ignore
    }
}

// Reload/reauth survival, in precedence order:
//   1. `?next=` in the URL (same-tab reload after we mirrored the SDK context)
//   2. localStorage (survives SSO polling's window.location.href and OTP verify's
//      Inertia redirect — both unmount MSTeamsLanding without touching the URL bar)
// The SDK context IIFE below overwrites whichever we seed here if it resolves with a
// fresh subPageId.
const urlNext = new URLSearchParams(window.location.search).get("next");
const validatedFromUrl = safeSubPagePath(urlNext, base_url);
if (validatedFromUrl) {
    deepLinkPath.value = validatedFromUrl;
} else {
    const validatedFromStorage = loadStoredDeepLinkPath();
    if (validatedFromStorage) {
        deepLinkPath.value = validatedFromStorage;
    }
}

const token = computedAsync(getClientSideToken);

// Read subPageId from the Teams SDK context and, if it validates as a same-origin path,
// use it as the post-login redirect target — takes precedence over ms_teams_next_path.
// Also mirrors the path into `?next=` so a mid-flow page reload doesn't lose the target,
// and logs a click event so bounces (users who never complete auth) are still tracked.
// Runs in parallel with token acquisition and never blocks the login flow: if the SDK is
// unavailable, the context fails, or the value doesn't validate, we fall back to the
// default redirect chain.
(async () => {
    try {
        await app.initialize();
        const context = await app.getContext();
        const path = safeSubPagePath(context?.page?.subPageId, base_url);
        if (path) {
            deepLinkPath.value = path;
            storeDeepLinkPath(path);
            const url = new URL(window.location.href);
            url.searchParams.set("next", path);
            window.history.replaceState({}, "", url);
            logUserInteraction("teams_notification_click", { deepLinkPath: path });
        }
    } catch (error) {
        logError(error);
    }
})();

async function getClientSideToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("mst_token");

    if (urlToken) {
        urlParams.delete("mst_token");
        const newUrl = window.location.pathname + (urlParams.toString() ? "?" + urlParams.toString() : "");
        window.history.replaceState({}, "", newUrl);
        return urlToken;
    }

    // If we're not running inside the Teams host — e.g. the "Go to website" tab option opened this
    // page as a standalone browser tab — the Teams SDK can't authenticate. Redirect to the standard
    // web login (SSO/magic-link, which works outside Teams) instead of attempting SDK auth and
    // showing a dead-end error. isInTeams() covers both desktop (iframe) and mobile (webview),
    // unlike a bare isInIFrame() check. mst_token logins are handled above and skip this entirely.
    if (!(await isInTeams())) {
        const nextUrl = urlParams.get("next");
        globalThis.location.replace(`/accounts/login/${nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : ""}`);
        return;
    }

    sdkError.value = null;

    try {
        await app.initialize();
        return await authentication.getAuthToken();
    } catch (error) {
        logError(error);
        sdkError.value = error;
    }
}

const should_show_terms = computedAsync(async () => {
    if (!token.value) {
        return false;
    }
    if (!loginCheckComplete.value) {
        return false;
    }
    // terms_are_required comes directly from the login endpoint for first-time OTP users
    if (terms_are_required) {
        modal.value = !!modal_terms;
        return true;
    }

    // Probe only - never send consent from here. This runs inside a computed, so passing
    // `terms.value` would both make the checkbox a reactive dependency (re-firing this on every
    // tick) and record consent as a side effect of rendering. Acceptance is posted explicitly by
    // login()/verifyOTP() with the document id the server offered.
    const { terms_required, modal_terms: modal_from_response, error } = await postTermsOfConsent(token.value, false);
    if (error) {
        sdkError.value = error;
        return false;
    }
    modal.value = !!modal_from_response;
    return terms_required;
});

// Driven off the same state as the terms block itself, so it covers every way the prompt
// arrives: the login-flow props, the probe, and a re-prompt carried on a rejected claim
// (postTermsOfConsent refreshes reconsentReason from the response payload).
const termsAnnouncement = computed(() => (should_show_terms.value ? termsConsentAnnouncement(reconsentReason.value) : ""));

// Inertia partial reloads (router.post defaults to preserveState: true) update props without
// remounting, so the setup-time snapshots above would otherwise stay stale and we would submit a
// null document id. Re-adopt the server-controlled policy whenever fresh props arrive.
watch(
    () => [terms_document_id, org_terms_content, modal_terms, reconsent_reason],
    ([documentId, content, isModal, reason]) => {
        if (documentId) {
            termsDocumentId.value = documentId;
        }
        if (content) {
            organization_terms_content.value = content;
        }
        modal.value = !!isModal;
        if (reason) {
            reconsentReason.value = reason;
        }
    },
);

async function postTermsOfConsent(token, consent, documentId = null) {
    loading.value = true;
    try {
        const response = await fetch("/microsoft-teams/terms-of-consent/", {
            method: "POST",
            headers: {
                "X-CSRFToken": getCookie("csrftoken"),
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ token, consent, terms_document_id: documentId }),
        });
        const data = await response.json().catch(() => ({}));

        if (data.redirect) {
            globalThis.location.href = data.redirect;
            return { terms_required: false, redirected: true };
        }

        // The server re-issues the current policy alongside a rejection, so adopting it here is
        // what lets a stale or missing document id self-heal on the next submission.
        if (data.terms_document_id) {
            termsDocumentId.value = data.terms_document_id;
        }
        if (data.toc_content) {
            organization_terms_content.value = data.toc_content;
        }
        if (data.reconsent_reason) {
            reconsentReason.value = data.reconsent_reason;
        }

        if (!response.ok) {
            const message = data.error || `HTTP ${response.status}: ${response.statusText}`;
            logError(message, { sendToSlack: true });
            return { error: message };
        }
        return {
            terms_required: data.terms_required,
            terms_document_id: data.terms_document_id,
            modal_terms: data.modal_terms,
        };
    } catch (error) {
        logError(error, { sendToSlack: true });
        return { error: "Failed to fetch terms of consent." };
    } finally {
        loading.value = false;
    }
}

watch([token, () => in_eu_server], async ([newToken]) => {
    if (!newToken) {
        return;
    }

    loading.value = true;

    try {
        const response = await fetch("/microsoft-teams/login-check/", {
            method: "GET",
            headers: { "X-CSRFToken": getCookie("csrftoken"), "X-MST-Token": newToken },
        });

        const data = await response.json();

        if (data.error) {
            logError(data.error, { sendToSlack: true });
            loading.value = false;
            return;
        }

        if (data.maintenance_message) {
            maintenance_message.value = data.maintenance_message;
            loading.value = false;
            return;
        }

        if (data.redirect) {
            const redirectUrl = new URL(data.redirect, base_url);

            if (redirectUrl.origin !== base_url) {
                redirectUrl.searchParams.set("mst_token", newToken);
                // Cross-origin cluster handoff (e.g. US → EU when the user's org lives in
                // the other region). localStorage is per-origin and this is a hard
                // navigation out of the Teams deep-link context, so both persistence
                // channels are lost. Include the validated deeplink as ?next= so the
                // target cluster's MSTeamsLanding rehydrates deepLinkPath from the URL
                // on mount (see the setup block above).
                if (deepLinkPath.value) {
                    redirectUrl.searchParams.set("next", deepLinkPath.value);
                    clearStoredDeepLinkPath();
                }
                const finalUrl = redirectUrl.href;
                window.location.href = finalUrl;

                return;
            }

            if (!data.redirect.includes("/microsoft-teams/login")) {
                // Preserve token in URL for same-origin redirects to prevent token loss
                // when user needs to re-authenticate
                const targetUrl = deepLinkPath.value || next_path || data.redirect;
                const targetUrlWithToken = new URL(targetUrl, base_url);
                targetUrlWithToken.searchParams.set("mst_token", newToken);

                // We're about to use the pending deep-link — clear the persisted copy so a
                // future re-mount of this page (post-logout, etc.) doesn't reuse a stale target.
                if (deepLinkPath.value) {
                    clearStoredDeepLinkPath();
                }

                router.visit(targetUrlWithToken.pathname + targetUrlWithToken.search, {
                    replace: true,
                });
                return;
            }
        }

        loginCheckComplete.value = true;
        loading.value = false;
    } catch (error) {
        console.error("Error during login check:", error);
        loading.value = false;
    }
});

function logReportIssue() {
    logUserInteraction("feedback_link_clicked");
}

async function recordTermsAcceptance() {
    consentError.value = null;
    const submit = () => postTermsOfConsent(token.value, true, termsDocumentId.value);
    const attemptedDocumentId = termsDocumentId.value;
    let response = await submit();

    // A missing or stale document id comes back as a re-prompt (or a rejection) carrying the
    // current policy, which postTermsOfConsent adopts. Retry once if we now hold a new document.
    if (!response.redirected && termsDocumentId.value && termsDocumentId.value !== attemptedDocumentId) {
        response = await submit();
    }
    if (!response.error && response.terms_required) {
        return { error: "We couldn't record your consent. Please reload the page and try again." };
    }
    return response;
}

async function login() {
    loading.value = true;
    if (should_show_terms.value && terms.value) {
        const consentResponse = await recordTermsAcceptance();
        if (consentResponse.error || consentResponse.redirected) {
            consentError.value = consentResponse.error || null;
            loading.value = false;
            return;
        }
    }
    const loginMethodResponse = await fetch(`/microsoft-teams/get-login-method/`, {
        method: "GET",
        headers: { "X-CSRFToken": getCookie("csrftoken"), "X-MST-Token": token.value },
    });
    if (loginMethodResponse) {
        const { method, handle } = await loginMethodResponse.json();
        if (method === "sso") {
            await loginViaSSO(handle);
        } else {
            await loginViaEmail();
        }
    } else {
        sdkError.value = "Failed to determine login method.";
        logError(sdkError.value, { sendToSlack: true });
    }
    loading.value = false;
}

// `handle` stands in for the access token in the popup URL. window.open can only issue a GET, and
// the ~2.2 KB Entra JWT would push that URL past the 2048-character cap managed browsers enforce —
// silently truncating the signature (BRA-895). The handle keeps the URL under 100 characters, so
// the call below stays exactly the shape Teams already knows how to host.
async function loginViaSSO(handle) {
    loading.value = true;

    if (!handle) {
        sdkError.value = "Failed to start SSO authentication.";
        logError("[ms-teams] login method was sso but no OAuth handle was issued", { sendToSlack: true });
        loading.value = false;
        return;
    }

    try {
        globalThis.open(`${base_url}/microsoft-teams/oauth/?h=${handle}`, SSO_WINDOW_NAME, "width=720,height=600,isExternal=true");
    } catch (error) {
        logError(error, { sendToSlack: true });
        sdkError.value = "Failed to start SSO authentication.";
        loading.value = false;
        return;
    }
    // NOTE: MS Teams docs here https://learn.microsoft.com/en-us/microsoftteams/platform/tabs/how-to/authentication/auth-oauth-provider
    // and here https://learn.microsoft.com/en-us/microsoftteams/platform/tabs/how-to/authentication/auth-flow-tab
    // suggest a few different things that do not work:
    // - use placeholders in the url to be replaced by the msteams-js library (does not work)
    // - call msteams-js library authentication.notifySuccess() from the external OAuth window (attempted in multiple ways, does not work)
    // therefore, we use a polling mechanism to check the status of the authentication.

    const MAX_POLL_ATTEMPTS = 300;
    let pollAttempts = 0;
    const interval = setInterval(() => {
        if (pollAttempts >= MAX_POLL_ATTEMPTS) {
            clearInterval(interval);
            loading.value = false;
            return;
        }
        pollAttempts++;
        fetch(`/microsoft-teams/oauth-poll/`, {
            method: "GET",
            headers: {
                "X-CSRFToken": getCookie("csrftoken"),
                "X-MST-Token": token.value,
            },
        })
            .then((response) => {
                return response.json();
            })
            .then((data) => {
                if (data.status === "pending") {
                    return;
                } else if (data.status === "success" && data.redirect) {
                    // Server's magic-link URL honors ?next= (accounts.views.magic_link_login),
                    // so append the validated deeplink so SSO users land on their target
                    // instead of the default post-login destination.
                    let target = data.redirect;
                    if (deepLinkPath.value) {
                        const magicUrl = new URL(data.redirect, base_url);
                        magicUrl.searchParams.set("next", deepLinkPath.value);
                        target = magicUrl.href;
                        clearStoredDeepLinkPath();
                    }
                    window.location.href = target;
                } else if (data.status === "error") {
                    logError(data.error, { sendToSlack: true });
                    sdkError.value = "Error authenticating via SSO.";
                }
                clearInterval(interval);
                loading.value = false;
            });
    }, 1000);
}

// This was implemented before our expansion of useFetch
async function postRequest(path, body, onSuccess) {
    loading.value = true;
    return router.post(path, body, {
        headers: {
            "X-CSRFToken": getCookie("csrftoken"),
        },
        onSuccess,
        onFinish: () => {
            loading.value = false;
        },
        only: ["error", "message", "terms_are_required", "org_terms_content", "terms_document_id", "modal_terms", "reconsent_reason"],
        exclude: ["feedback_mailto"],
    });
}

function startTimer() {
    timer.value = 60;
    const interval = setInterval(() => {
        if (timer.value > 0) {
            timer.value--;
        } else {
            clearInterval(interval);
        }
    }, 1000);
}

async function loginViaEmail() {
    // The server returns the same confirmation for every code, so a further request would leave
    // the live region unchanged and silent. Empty it before the request, and word the repeat
    // differently: VoiceOver also skips an announcement identical to the region's previous one.
    const prefix = message ? "Another code was sent. " : "";
    messageAnnouncement.value = "";
    await postRequest("", { token: token.value }, (page) => {
        messageAnnouncement.value = page.props.message ? `${prefix}${page.props.message}` : "";
    });
    startTimer();
}

async function verifyOTP() {
    if (should_show_terms.value && terms.value) {
        const consentResponse = await recordTermsAcceptance();
        if (consentResponse.error || consentResponse.redirected) {
            consentError.value = consentResponse.error || null;
            return;
        }
    }
    const body = {
        token: token.value,
        otp: otp.value,
    };
    if (deepLinkPath.value) {
        // Server threads this into the magic-link redirect (accounts.views.magic_link_login
        // honors ?next=), so OTP users land on their deeplink target after auth completes.
        body.next = deepLinkPath.value;
        clearStoredDeepLinkPath();
    }
    postRequest("/microsoft-teams/verify-otp/", body);
}
</script>
